import { Alert, Box, Column, SectionHeading } from "@hightouch/htui";
import * as Sentry from "src/lib/monitoring";
import { FunnelDataSchema } from "@hightouch/lib/llm/schema/components/schemas";
import { transformFunnelToGraphData } from "./funnel-transformer";
import { Card } from "src/components/card";
import type { ReactNode } from "react";
import { FunnelGraph } from "src/components/analytics/funnel";
import {
  COMPACT_CHART_HEIGHT,
  DEFAULT_CHART_HEIGHT,
  parseJsonChildren,
} from "./chart-utils";

/**
 * FunnelChart component for displaying conversion funnel data
 * from LLM agents. Uses the existing FunnelGraph component.
 *
 * Data is passed as children content (JSON string).
 */
export const FunnelChart = ({
  children,
  compact = false,
}: {
  children: ReactNode;
  compact?: boolean;
}) => {
  try {
    const funnelData = parseJsonChildren(children, FunnelDataSchema);
    const graphData = transformFunnelToGraphData(funnelData);

    const graph = (
      <FunnelGraph
        graphData={graphData}
        showLegend={true}
        height={compact ? COMPACT_CHART_HEIGHT : DEFAULT_CHART_HEIGHT}
        // Report funnels render statically (matching the line/bar charts,
        // which set isAnimationActive={false}); the entry animation would
        // otherwise be captured mid-flight and print as empty bars.
        disableAnimation
      />
    );

    if (compact) {
      return <Box py={2}>{graph}</Box>;
    }

    return (
      <Card p={4} my={4}>
        <Column gap={2}>
          <SectionHeading>{funnelData.config.title}</SectionHeading>
          {graph}
        </Column>
      </Card>
    );
  } catch (error) {
    Sentry.captureException(error);
    return (
      <Alert
        type="error"
        title="Chart Error"
        message="Unable to render funnel chart, invalid data."
        my={4}
      />
    );
  }
};
